/**
 * Program API Client - Frontend API calls for programs
 */

import type { Program, ProgramFilters, ProgramFilterOptions, ProgramBrowseResult } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class ProgramAPI {
  /**
   * Browse programs with filters
   */
  async browsePrograms(filters: ProgramFilters): Promise<ProgramBrowseResult> {
    const params = new URLSearchParams();

    if (filters.field) params.set('field', filters.field);
    if (filters.degreeType) params.set('degree_type', filters.degreeType);
    if (filters.universityId) params.set('university_id', filters.universityId.toString());
    if (filters.country) params.set('country', filters.country);
    if (typeof filters.isOnline === 'boolean') params.set('is_online', filters.isOnline.toString());
    if (filters.page) params.set('page', filters.page.toString());
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.sort) params.set('sort', filters.sort);

    const response = await fetch(`${API_BASE}/api/program/browse?${params}`);
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to browse programs');
    }

    return result.data;
  }

  /**
   * Get filter options
   */
  async getFilterOptions(): Promise<ProgramFilterOptions> {
    const response = await fetch(`${API_BASE}/api/program/filters`);
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to get filter options');
    }

    return result.data;
  }

  /**
   * Get program by ID or slug
   */
  async getProgramById(idOrSlug: number | string): Promise<Program> {
    const response = await fetch(`${API_BASE}/api/program/${idOrSlug}`);
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to get program');
    }

    return result.data;
  }

  /**
   * Get programs by university
   */
  async getProgramsByUniversity(universityId: number): Promise<Program[]> {
    const response = await fetch(`${API_BASE}/api/program/university/${universityId}`);
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to get programs');
    }

    return result.data;
  }

  /**
   * Search programs
   */
  async searchPrograms(query: string, limit: number = 10): Promise<Program[]> {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString()
    });

    const response = await fetch(`${API_BASE}/api/program/search?${params}`);
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to search programs');
    }

    return result.data;
  }
}

export const programAPI = new ProgramAPI();
